import React from "react";
import { StatCard } from "../StatCard";
import { BranchComparisonChart } from "../BranchComparisonChart";
import { PeriodSnapshotData } from "@/hooks/usePeriodSnapshot";
import { Wallet, GraduationCap, AlertTriangle } from "lucide-react";

interface PeriodSnapshotDashboardProps {
  data: PeriodSnapshotData;
  startDate: string;
  endDate: string;
}

export function PeriodSnapshotDashboard({ data, startDate, endDate }: PeriodSnapshotDashboardProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const online = data.branches.online || { teaching_hours: 0, salary: 0, sudden_leave_hours: 0 };
  const siam = data.branches.siam || { teaching_hours: 0, salary: 0, sudden_leave_hours: 0 };
  const ngam = data.branches.ngamwongwan || { teaching_hours: 0, salary: 0, sudden_leave_hours: 0 };

  return (
    <div className="space-y-6">
      {/* Overview */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">📊</span> ภาพรวม ({data.row_count.toLocaleString()} รายการ)
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            title="เงินเดือนรวม"
            value={formatCurrency(data.total_salary)}
            suffix="฿"
            icon={<Wallet className="h-5 w-5" />}
          />
          <StatCard
            title="ชั่วโมงสอนรวม"
            value={data.total_teaching_hours.toLocaleString("th-TH")}
            suffix="ชม."
            icon={<GraduationCap className="h-5 w-5" />}
          />
          <StatCard
            title="ลากระทันหันรวม"
            value={data.total_sudden_leave_hours.toLocaleString("th-TH")}
            suffix="ชม."
            inverseColors={true}
            icon={<AlertTriangle className="h-5 w-5" />}
          />
        </div>
      </div>

      {/* Salary by branch */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">💰</span> เงินเดือนแยกตามสาขา
        </h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard title="Online" value={formatCurrency(online.salary)} suffix="฿" />
          <StatCard title="Siam" value={formatCurrency(siam.salary)} suffix="฿" />
          <StatCard title="Ngamwongwan" value={formatCurrency(ngam.salary)} suffix="฿" />
        </div>
      </div>

      {/* Teaching hours by branch */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">📚</span> ชั่วโมงสอนแยกตามสาขา
        </h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard title="Online" value={online.teaching_hours.toLocaleString("th-TH")} suffix="ชม." />
          <StatCard title="Siam" value={siam.teaching_hours.toLocaleString("th-TH")} suffix="ชม." />
          <StatCard title="Ngamwongwan" value={ngam.teaching_hours.toLocaleString("th-TH")} suffix="ชม." />
        </div>
      </div>

      {/* Sudden leave by branch */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">⚠️</span> ลากระทันหันแยกตามสาขา
        </h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard title="Online" value={online.sudden_leave_hours.toLocaleString("th-TH")} suffix="ชม." inverseColors={true} />
          <StatCard title="Siam" value={siam.sudden_leave_hours.toLocaleString("th-TH")} suffix="ชม." inverseColors={true} />
          <StatCard title="Ngamwongwan" value={ngam.sudden_leave_hours.toLocaleString("th-TH")} suffix="ชม." inverseColors={true} />
        </div>
      </div>

      {/* Branch Chart */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">🏢</span> เปรียบเทียบสาขา
        </h3>
        <BranchComparisonChart
          online={{ hours: online.teaching_hours, salary: online.salary }}
          siam={{ hours: siam.teaching_hours, salary: siam.salary }}
          ngamwongwan={{ hours: ngam.teaching_hours, salary: ngam.salary }}
        />
      </div>
    </div>
  );
}
